import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader, TrendingUp, Scale, Check, Star } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import ListingCard from "./ListingCard";
import { useTheme } from "../context/ThemeContext";
import { getMyDirectoryListing } from "../services/directoryService";
import "./Directory.css";
import "./MyDirectoryListing.css";

/** Where "Claim Your State" sends its pre-filled enquiry. */
const BANNER_EMAIL = "admin@lawfusion.ai";

/**
 * The attorney's own view of their directory listing.
 *
 * Only the live/approved state is reachable: a directory account does not
 * exist until an admin approves the submission, so there is no pending or
 * rejected version of this page to build.
 */
/**
 * The example Featured listing shown beside the upsell.
 *
 * Rendered through the same ListingCard as a real listing, so what an attorney
 * is being sold is exactly what they would get — a hand-built mock would drift
 * the moment the real card changes. Only the badge differs, and it says
 * "Sample" so nobody reads this as their own listing.
 *
 * The role deliberately names both audiences; a real listing only ever carries
 * one.
 */
const SAMPLE_FEATURED_LISTING = {
  _id: "sample-featured",
  firstName: "Priya",
  lastName: "Raman",
  role: "Attorney/Paralegal",
  firmName: "Raman Family Law Group",
  city: "San Diego",
  state: "California",
  yearsInPractice: "18",
  practiceAreas: [
    "Family Law",
    "Estate Planning / Probate",
    "Civil Litigation",
  ],
  freeConsultation: true,
  featured: true,
  photoUrl: "/featured-example-headshot.jpg",
  emailPublic: "priya@example.com",
  phonePublic: "555-0100",
  website: "https://example.com",
  verified: true,
};

export default function MyDirectoryListing() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const mine = await getMyDirectoryListing(authHeaders);
      if (!mine) {
        // No listing to manage — the setup form is the only useful destination.
        navigate("/directory?join=1", { replace: true });
        return;
      }
      setListing(mine);
    } catch (err) {
      setError(err.message || "We could not load your listing just now.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const isFeatured = !!listing?.featured;
  const stateName = listing?.state || "your state";

  const bannerMailto = `mailto:${BANNER_EMAIL}?subject=${encodeURIComponent(
    `Exclusive Statewide Banner — ${stateName}`,
  )}&body=${encodeURIComponent(
    `Hello LawFusion team,\n\nI would like to claim the exclusive statewide banner placement for ${stateName}.\n\nName: ${listing?.firstName || ""} ${listing?.lastName || ""}\nFirm: ${listing?.firmName || ""}\n\nPlease send me the details.\n\nThank you.`,
  )}`;

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="My Directory Listing | LawFusion"
        path="/my-listing"
        description="Manage your LawFusion attorney directory listing."
        noIndex={true}
      />
      <Header />

      <main className="mdl-page">
        {loading ? (
          <div className="mdl-loading">
            <Loader size={22} className="fa-spin" />
            <p>Loading your listing…</p>
          </div>
        ) : error ? (
          <div className="mdl-error">
            <p>{error}</p>
            <button type="button" className="btn-join" onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          listing && (
            <>
              <div className="mdl-head">
                <h1>My Directory Listing</h1>
                <span className="mdl-status-pill">Live</span>
              </div>
              <p className="mdl-sub">
                This is how your listing appears to potential clients in the
                LawFusion directory.
              </p>

              <ListingCard listing={listing} className="mdl-card" />

              <div className="mdl-actions">
                <Link to="/my-listing/edit" className="mdl-btn mdl-btn-primary">
                  Edit Listing
                </Link>
                {/* The id takes the visitor to their own card rather than to
                    the top of a directory page they then have to scan. */}
                <Link
                  to={`/directory?search=${encodeURIComponent(
                    `${listing.firstName || ""} ${listing.lastName || ""}`.trim(),
                  )}&highlight=${encodeURIComponent(listing._id || "")}`}
                  className="mdl-btn mdl-btn-secondary"
                >
                  View Public Listing
                </Link>
              </div>

              <section className="mdl-section-head">
                <h2>Ways to Get More Visibility</h2>
                <p>
                  Optional placements available to attorneys and paralegals in
                  the LawFusion directory.
                </p>
              </section>

              {isFeatured ? (
                <section className="mdl-active-featured">
                  <span className="mdl-active-icon mdl-active-icon-gold">
                    <Star size={18} />
                  </span>
                  <div>
                    <div className="mdl-active-title">
                      Featured Listing — Active
                    </div>
                    <p>
                      Your listing appears directly in consumer search results
                      for your practice areas and state.
                    </p>
                  </div>
                </section>
              ) : (
                <section className="mdl-upsell">
                  <div className="mdl-upsell-pitch">
                    <div className="mdl-upsell-icon">
                      <TrendingUp size={30} />
                    </div>
                    <h3>Get More Visibility</h3>
                    <p>
                      When a consumer searches for your practice area and state,
                      Featured listings appear directly in their results — not
                      just in a separate section.
                    </p>
                  <ul className="mdl-upsell-points">
                    <li>
                      <Check size={16} /> Your photo on the listing, with a
                      Featured badge
                    </li>
                    <li>
                      <Check size={16} /> Placement inside consumer search
                      results
                    </li>
                    <li>
                      <Check size={16} /> Included with every paid LawFusion
                      plan
                    </li>
                  </ul>
                  </div>

                  <div className="mdl-upsell-sample" aria-label="Example of a Featured listing">
                    <ListingCard
                      listing={SAMPLE_FEATURED_LISTING}
                      badgeLabel="Sample Featured Listing"
                      className="mdl-sample-card"
                    />
                  </div>

                  <Link to="/pricing" className="mdl-btn mdl-btn-secondary mdl-btn-wide">
                    Learn About Featured Listings
                  </Link>
                </section>
              )}

              <section className="mdl-ad">
                <span className="mdl-ad-label">Advertisement</span>
                <div className="mdl-ad-body">
                  <div className="mdl-ad-icon">
                    <Scale size={22} />
                  </div>
                  <div>
                    <p className="mdl-ad-eyebrow">Exclusive Statewide Banner</p>
                    <h3>Be the Exclusive Banner Advertiser in Your State</h3>
                    <p className="mdl-ad-copy">
                      Your banner will be shown on LawFusion's consumer
                      legal-question page to users who select your state.
                      $1,000/month · one advertiser per state · no rotation.
                    </p>
                    <p className="mdl-ad-fine">
                      Separate advertising placement — not a featured directory
                      listing. Subject to availability.
                    </p>
                  </div>
                </div>
                <a href={bannerMailto} className="mdl-btn mdl-btn-secondary">
                  Claim Your State
                </a>
              </section>
            </>
          )
        )}
      </main>

      <Footer />
    </div>
  );
}
